import React, { useState, useEffect } from 'react';
import { usePoints } from '../../context/PointsContext';
import PointsConfirmationModal from './PointsConfirmationModal';
import InsufficientPointsModal from './InsufficientPointsModal';
import styles from './UsageIndicator.module.css';

interface UsageIndicatorProps {
  label: string;
  used?: number;
  limit?: number | 'unlimited';
  icon: string;
  hasAppSubscription?: boolean;
  featureType?: string; // For points cost calculation
  onUseFeature?: (featureType: string) => Promise<void>; // Optional callback for feature usage
  pointsOnly?: boolean; // NEW: Hide traditional usage numbers for points users
}

const UsageIndicator: React.FC<UsageIndicatorProps> = ({ 
  label, 
  used = 0, 
  limit = 0, 
  icon, 
  hasAppSubscription,
  featureType,
  onUseFeature,
  pointsOnly = false // NEW: Points-only mode
}) => {
  const { isEnabled, checkCost, balance } = usePoints();
  const [pointsCost, setPointsCost] = useState<number | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showInsufficientModal, setShowInsufficientModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (isEnabled && featureType) {
      loadPointsCost();
    }
  }, [isEnabled, featureType]);

  const loadPointsCost = async () => {
    if (!featureType) return;
    
    try {
      const costInfo = await checkCost(featureType, 1);
      setPointsCost(costInfo?.totalCost || null);
    } catch (error) {
      console.error('Error loading points cost:', error);
    }
  };

  const handleUseFeature = () => {
    if (!isEnabled || !pointsCost || !balance) {
      // Traditional usage - redirect to feature or show upgrade
      if (limit === 0 || (typeof limit === 'number' && limit <= 0)) {
        window.location.href = '/pricing';
      }
      return;
    }

    // Points system - show confirmation modal
    if (balance.pointsRemaining >= pointsCost) {
      setShowConfirmModal(true);
    } else {
      setShowInsufficientModal(true);
    }
  };

  const handleConfirmUsage = async () => {
    if (!featureType || !onUseFeature) return;
    
    setIsProcessing(true);
    try {
      await onUseFeature(featureType);
      setShowConfirmModal(false);
    } catch (error) {
      console.error('Error using feature:', error);
      // Handle error - maybe show error toast
    } finally {
      setIsProcessing(false);
    }
  };

  const getUsagePercentage = (used: number, limit: number | 'unlimited'): number => {
    if (limit === 'unlimited') return 0;
    if (typeof limit !== 'number' || limit <= 0) return 0;
    if (typeof used !== 'number' || used < 0) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage: number): string => {
    if (percentage >= 90) return 'critical';
    if (percentage >= 75) return 'warning'; 
    if (percentage >= 50) return 'moderate';
    return 'good';
  };

  const getStatusColor = (percentage: number): string => {
    if (percentage >= 90) return '#ef4444'; // Red
    if (percentage >= 75) return '#f59e0b'; // Orange
    if (percentage >= 50) return '#3b82f6'; // Blue
    return '#10b981'; // Green
  };

  const percentage = getUsagePercentage(used, limit);
  const colorClass = getUsageColor(percentage);
  const color = getStatusColor(percentage);
  const isUnlimited = limit === 'unlimited';
  const isAvailable = limit !== 0 && (typeof limit !== 'number' || limit > 0);

  return (
    <>
      <div className={`${styles.usageCard} ${!isAvailable ? styles.unavailable : ''}`}>
        <div className={styles.usageHeader}>
          <div className={`${styles.usageIcon} ${styles[colorClass]}`}>{icon}</div>
          <div className={styles.usageInfo}>
            <h3 className={styles.usageLabel}>{label}</h3>
            {hasAppSubscription && (
              <span className={styles.appSubscriptionBadge}>Individual Subscription</span>
            )}
            {isEnabled && pointsCost && (
              <span className={styles.pointsCost}>
                <span className={styles.pointsIcon}>💎</span>
                {pointsCost.toLocaleString()} points
              </span>
            )}
          </div>
          <div className={styles.usageNumbers}>
            {pointsOnly ? (
              // Points-only mode: Show "Use Now" or status instead of usage numbers
              <div className={styles.pointsOnlyStatus}>
                <span className={styles.readyToUse}>Ready</span>
              </div>
            ) : isUnlimited ? (
              <span className={styles.unlimitedText}>∞</span>
            ) : (
              <>
                <span className={`${styles.usedCount} ${styles[colorClass]}`}>{used.toLocaleString()}</span>
                <span className={styles.separator}>/</span>
                <span className={styles.limitCount}>{(limit as number).toLocaleString()}</span>
              </>
            )}
          </div>
        </div>
        
        {!pointsOnly && (
          <div className={styles.progressContainer}>
            <div className={styles.progressBar}>
              <div 
                className={`${styles.progressFill} ${styles[colorClass]}`}
                style={{ 
                  width: `${percentage}%`,
                }}
              >
                <div className={styles.progressGlow}></div>
              </div>
            </div>
            <div className={styles.progressInfo}>
              <span className={styles.progressText}>
                {isUnlimited ? 'Unlimited' : 
                 limit === 0 ? 'Not Available' : 
                 `${percentage.toFixed(0)}% used`}
              </span>
              {isAvailable && onUseFeature && (
                <button 
                  className={`${styles.useButton} ${styles[colorClass]}`}
                  onClick={handleUseFeature}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <div className={styles.spinner}></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <span className={styles.useIcon}>⚡</span>
                      Use Now
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Points-only mode: Show clean info without action buttons */}
        {pointsOnly && (
          <div className={styles.pointsOnlyInfo}>
            <div className={styles.featureDescription}>
              Ready to use · {pointsCost?.toLocaleString() || 0} points per use
            </div>
          </div>
        )}

        {!pointsOnly && (
          <div className={styles.statusInfo}>
            {isUnlimited ? (
              <span className={styles.statusText}>
                <span className={styles.statusIcon}>✨</span>
                Unlimited usage available
              </span>
            ) : limit === 0 ? (
              <span className={styles.unavailableText}>
                <span className={styles.statusIcon}>🔒</span>
                Upgrade to access this feature
              </span>
            ) : (
              <span className={styles.remainingText}>
                <span className={styles.statusIcon}>📊</span>
                {Math.max(0, (typeof limit === 'number' ? limit : 0) - used).toLocaleString()} remaining this month
              </span>
            )}
          </div>
        )}
      </div>

      {/* Modals for points system */}
      <PointsConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmUsage}
        featureName={label}
        pointsCost={pointsCost || 0}
        remainingPoints={balance?.pointsRemaining || 0}
        loading={isProcessing}
      />

      <InsufficientPointsModal
        isOpen={showInsufficientModal}
        onClose={() => setShowInsufficientModal(false)}
        featureName={label}
        pointsNeeded={pointsCost || 0}
        currentPoints={balance?.pointsRemaining || 0}
      />
    </>
  );
};

export default UsageIndicator;